import ArrowBackIosNewIcon from "@mui/icons-material/ArrowBackIosNew";
import {
  Button,
  Checkbox,
  Divider,
  IconButton,
  InputLabel,
  Stack,
  TextField,
  Tooltip,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import { ReactComponent as HelpIcon } from "images/help-icon.svg";
import { birthDateValidation, phoneNumberValidation } from "models/constants";
import { customColors } from "models/customColors";
import { createRef, useState } from "react";
import { FormDateSelector } from "UI/FormDateSelector";
import { FormMaskedTextField } from "UI/FormMaskedTextField";
import { FormSelectField } from "UI/FormSelectField";
import { FormTextField } from "UI/FormTextField";
import { Spinner } from "UI/Spinner";
import { required } from "utils";
import { DialogWindow } from "../DialogWindow";
import { Error } from "../ReferralForm/ReferralForm.common";
import { Captcha } from "components/Captcha";
import { InfoDriver } from "./InfoDriver";

const UPLOAD_TOTAL_FILES_SIZE_LIMIT_IN_BYTES = 100 * 1024 * 1024;

export const EmbeddedReferralDetailsForm = ({
  originator,
  recipient,
  handleResetAndBack,
  providerOptions,
  locationOptions,
  control,
  watch,
  setValue,
  getValues,
  trigger,
  reset,
  isButtonEnabled,
  loading,
  setLoading,
  onQuestionSubmit,
  handleClose = null,
  dashboardAccessToken = null,
  isLocationsPresent,
  isMoreThanOneProvider,
  setError,
  setFormError,
  clearErrors,
  reloadAuthData,
  hanldeLogout,
  loginState,
  setLoginState,
  setProviders,
}) => {
  const [fileError, setFileError] = useState(null);
  const [urgencyError, setUrgencyError] = useState(null);
  const [genderError, setGenderError] = useState(null);
  const [selectedFiles, setSelectedFiles] = useState([]);
  const [uploadedFileIds, setUploadedFileIds] = useState([]);
  const filesRef = createRef();

  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));

  const setGender = (value) => {
    setValue("gender", value);
    setGenderError(null);
  };

  const setUrgency = (value) => {
    setValue("urgency", value);
    setUrgencyError(null);
  };

  const clearForm = () => {
    reset();
    setUrgencyError(null);
    setGenderError(null);
    setSelectedFiles([]);
    setUploadedFileIds([]);
    if (filesRef.current) {
      filesRef.current.value = "";
    }
    setFileError(null);
  };

  const checkIfSizeIsTooLarge = (files) => {
    const totalSize = files.reduce((acc, file) => acc + file.size, 0);
    if (totalSize > UPLOAD_TOTAL_FILES_SIZE_LIMIT_IN_BYTES) {
      return true;
    }
    return false;
  };

  const onReferralSubmit = async () => {
    const values = getValues();
    let withErrors = false;

    if (!values?.gender) {
      setGenderError("is required");
      withErrors = true;
    }
    if (!values?.urgency) {
      setUrgencyError("is required");
      withErrors = true;
    }
    const valid = await trigger();
    if (!valid) {
      withErrors = true;
    }
    if (withErrors) return;

    onQuestionSubmit({ ...values, uploadedFileIds });
  };

  const handleFileChange = async (event) => {
    const files = Array.from(event.target.files);

    if (checkIfSizeIsTooLarge(files)) {
      setFileError(
        "Your total files size is larger than 100MB. Try to upload them one by one."
      );
      if (filesRef.current) {
        filesRef.current.value = "";
      }
      event.target.value = "";
      return;
    }

    setLoading(true);
    setFileError(null);

    try {
      const formData = new FormData();

      for (const newFile of files) {
        formData.append("dashboardAccessToken", dashboardAccessToken);
        const fileContents = [newFile];
        fileContents.forEach((file) => {
          formData.append("files[]", file);
        });
      }

      const response = await fetch(
        `${process.env.REACT_APP_API_V2_URL}/referral/registration/files/upload`,
        {
          method: "POST",
          body: formData,
        }
      );
      const result = await response.json();

      if (response?.status !== 200 || result?.error) {
        throw new Error(
          result?.message ?? result?.error ?? "Something went wrong"
        );
      }

      setSelectedFiles((prev) => [...prev, ...files]);
      setUploadedFileIds((prev) => [...prev, ...result]);
    } catch (error) {
      setFileError(error?.message ?? error?.error ?? "Something went wrong");
    } finally {
      setLoading(false);
      event.target.value = "";
    }
  };

  return (
    <DialogWindow
      handleClose={handleClose}
      withCloseButton={!!handleClose}
      title={
        <Stack direction={"row"} alignItems={"center"} gap={1}>
          <IconButton aria-label="close" onClick={handleResetAndBack}>
            <ArrowBackIosNewIcon />
          </IconButton>
          Digital Referral Form
        </Stack>
      }
      content={
        <Stack
          direction={isMobile ? "column" : "row"}
          justifyContent={"center"}
          alignItems={"top"}
          gap={2}
          pt={2}
        >
          <InfoDriver
            originator={originator}
            recipient={recipient}
            control={control}
            watch={watch}
            loading={loading}
            setLoading={setLoading}
            setError={setError}
            setFormError={setFormError}
            reloadAuthData={reloadAuthData}
            handleLogout={hanldeLogout}
            loginState={loginState}
            setLoginState={setLoginState}
            setValue={setValue}
            clearErrors={clearErrors}
            setProviders={setProviders}
          />
          <Divider orientation="vertical" flexItem />
          <Stack alignItems={"stretch"} gap={1}>
            {isMoreThanOneProvider && (
              <Stack mt={1} alignItems={"flex-start"} flex={1}>
                <InputLabel htmlFor="providerId">
                  Select Your Practice Provider
                </InputLabel>
                <FormSelectField
                  id="providerId"
                  name="providerId"
                  control={control}
                  options={providerOptions}
                  withNone={false}
                  rules={{ required }}
                  doNotSortOptions
                />
              </Stack>
            )}
            {isLocationsPresent && (
              <Stack alignItems={"flex-start"} flex={1}>
                <InputLabel htmlFor="locationId">
                  Select Recipient Location
                </InputLabel>
                <FormSelectField
                  id="locationId"
                  name="locationId"
                  control={control}
                  options={locationOptions}
                  withNone={false}
                  doNotSortOptions
                />
              </Stack>
            )}
            <div className="text-left">
              Patient Information
              <Divider />
            </div>
            <Stack
              direction={"row"}
              flexWrap={"wrap"}
              alignItems={"flex-start"}
              gap={4}
            >
              <Stack flex={1} gap={1}>
                <Stack alignItems={"flex-start"}>
                  <InputLabel htmlFor="speciality">
                    Patient Full Name
                  </InputLabel>
                  <FormTextField
                    id="patientName"
                    name="patientName"
                    control={control}
                    InputProps={{
                      autoComplete: "off",
                    }}
                    rules={{ required: "is required" }}
                  />
                </Stack>
                <Stack alignItems={"flex-start"}>
                  <InputLabel htmlFor="patientDob">Date of birth</InputLabel>
                  <FormDateSelector
                    id="patientDob"
                    name="patientDob"
                    control={control}
                    size="small"
                    rules={birthDateValidation}
                    maxDate={new Date()}
                  />
                </Stack>
                <Stack alignItems={"flex-start"}>
                  <InputLabel>Patient gender:</InputLabel>
                  <Stack direction={"row"} gap={2}>
                    <InputLabel htmlFor="male" sx={{ cursor: "pointer" }}>
                      <Stack direction={"row"} alignItems={"center"}>
                        <Checkbox
                          id="male"
                          checked={watch("gender") === "Male"}
                          onChange={() => setGender("Male")}
                        />
                        Male
                      </Stack>
                    </InputLabel>
                    <InputLabel htmlFor="female" sx={{ cursor: "pointer" }}>
                      <Stack direction={"row"} alignItems={"center"}>
                        <Checkbox
                          id="female"
                          checked={watch("gender") === "Female"}
                          onChange={() => setGender("Female")}
                        />
                        Female
                      </Stack>
                    </InputLabel>
                  </Stack>
                  {genderError && <Error>{genderError}</Error>}
                </Stack>
                <Stack alignItems={"flex-start"}>
                  <Stack direction={"row"} gap={0.5}>
                    <InputLabel htmlFor="state">
                      Attach patient records (optional)
                    </InputLabel>
                    <Tooltip title="Demographics, Clinical Notes, Imaging Results, Recent Labs, Copy of Insurance & ID, etc.">
                      <HelpIcon width={18} />
                    </Tooltip>
                  </Stack>
                  <TextField
                    type="file"
                    size="small"
                    error={!!fileError}
                    helperText={fileError}
                    inputRef={filesRef}
                    inputProps={{
                      multiple: true,
                      style: {
                        color: "gray",
                      },
                    }}
                    sx={{ width: "100%" }}
                    onChange={handleFileChange}
                  />
                  <Stack mt={1}>
                    {selectedFiles?.map((file) => (
                      <Stack
                        key={file.name}
                        direction={"row"}
                        justifyContent={"space-between"}
                        gap={1}
                        rowGap={0.5}
                        sx={{ fontSize: 12, color: customColors.mainColor }}
                      >
                        <div className="flex-1 break-all text-left">
                          {file.name}
                        </div>
                        <div className="text-gray-400">
                          {(file.size / 1024).toFixed(2)} KB
                        </div>
                      </Stack>
                    ))}
                  </Stack>
                </Stack>
              </Stack>
              <Stack flex={1} gap={1}>
                <Stack alignItems={"flex-start"}>
                  <InputLabel htmlFor="patientPhone">Patient Phone</InputLabel>
                  <FormMaskedTextField
                    id="patientPhone"
                    name="patientPhone"
                    control={control}
                    rules={phoneNumberValidation}
                    mask="999-999-9999"
                    InputProps={{
                      autoComplete: "off",
                    }}
                  />
                </Stack>
                <Stack alignItems={"flex-start"}>
                  <InputLabel htmlFor="patientLocation">
                    Where is the patient?
                  </InputLabel>
                  <FormSelectField
                    id="patientLocation"
                    name="patientLocation"
                    control={control}
                    rules={{ required: "is required" }}
                    options={{
                      Home: "Home",
                      Facility: "Facility",
                    }}
                  />
                </Stack>
                <Stack alignItems={"flex-start"}>
                  <InputLabel>Urgency:</InputLabel>
                  <Stack direction={"row"} gap={2}>
                    <InputLabel htmlFor="stat" sx={{ cursor: "pointer" }}>
                      <Stack direction={"row"} alignItems={"center"}>
                        <Checkbox
                          id="stat"
                          checked={watch("urgency") === "STAT"}
                          onChange={() => setUrgency("STAT")}
                        />
                        STAT
                      </Stack>
                    </InputLabel>
                    <InputLabel htmlFor="routine" sx={{ cursor: "pointer" }}>
                      <Stack direction={"row"} alignItems={"center"}>
                        <Checkbox
                          id="routine"
                          checked={watch("urgency") === "Routine"}
                          onChange={() => setUrgency("Routine")}
                        />
                        Routine
                      </Stack>
                    </InputLabel>
                  </Stack>
                  {urgencyError && <Error>{urgencyError}</Error>}
                </Stack>
                <Stack alignItems={"flex-start"}>
                  <InputLabel htmlFor="reason">
                    Reason for referral (optional)
                  </InputLabel>
                  <FormTextField
                    id="reason"
                    name="reason"
                    control={control}
                    InputProps={{
                      autoComplete: "off",
                    }}
                    multiline
                  />
                </Stack>
              </Stack>
            </Stack>
            {!originator && (
              <Captcha
                control={control}
                setValue={setValue}
                setError={setError}
              />
            )}
          </Stack>
          {loading && <Spinner />}
        </Stack>
      }
      actions={
        <Stack
          flex={1}
          direction={"row"}
          gap={1}
          justifyContent={"center"}
          alignItems={"center"}
        >
          <Button
            variant="outlined"
            color="primary"
            disabled={!isButtonEnabled || loading}
            onClick={clearForm}
            sx={{ padding: "8px 24px" }}
          >
            Clear
          </Button>
          <Button
            variant="contained"
            color="primary"
            disabled={!isButtonEnabled || loading}
            onClick={onReferralSubmit}
            sx={{ padding: "8px 24px" }}
          >
            Send Referral
          </Button>
        </Stack>
      }
    />
  );
};
